/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Image,
  FlatList,
  TextInput,
  Modal,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  AntDesign,
} from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import DateTimePicker from '@react-native-community/datetimepicker';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ===== API Configuration =====
const API_BASE_URL = 'https://api.jobeng.ai';

const getCompanyId = async () => {
  try {
    const userData = await AsyncStorage.getItem('currentUser');
    if (userData) {
      const user = JSON.parse(userData);
      return user.company_id || user.companyId || null;
    }
    const companyProfile = await AsyncStorage.getItem('companyProfile');
    if (companyProfile) {
      const company = JSON.parse(companyProfile);
      return company.id || company.company_id || null;
    }
    return null;
  } catch (e) {
    console.error('Error getting company_id:', e);
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  try {
    const companyId = await getCompanyId();
    // Split endpoint and query string
    const [path, queryString] = endpoint.split('?');
    // Route: auth/* goes to /api/auth/*, everything else to /api/data/{table}
    let baseUrl;
    if (path.startsWith('auth/')) {
      baseUrl = `${API_BASE_URL}/api/${path}`;
    } else {
      // Convert hyphens to underscores for DB table mapping
      const dbPath = path.replace(/-/g, '_');
      baseUrl = `${API_BASE_URL}/api/data/${dbPath}`;
    }
    const params = new URLSearchParams(queryString || '');
    if (companyId) params.set('company_id', companyId);
    const separator = params.toString() ? '?' : '';
    const url = `${baseUrl}${separator}${params.toString()}`;

    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
      ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};
// ===== End API Configuration =====


// JOBENG Logo component using the app icon
const JobengLogo = () => (
  <View style={styles.logoContainer}>
    <Image
      source={require('../../../../assets/jobeng_icon.png')}
      style={styles.logoImage}
      resizeMode="contain"
    />
    <Text style={styles.logoText}>JOBENG</Text>
  </View>
);

const RegulatoryUpdateDetailsScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { updateId } = route.params || {};

  const [loading, setLoading] = useState(true);
  const [updateData, setUpdateData] = useState(null);
  const [relatedCertifications, setRelatedCertifications] = useState([]);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionTitle, setActionTitle] = useState('');
  const [actionDueDate, setActionDueDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [markingAsRead, setMarkingAsRead] = useState(false);

  // Mock data for demonstration
  const mockUpdateData = {
    id: 'u1',
    title: 'New Health & Safety Regulations 2025',
    source: 'Health and Safety Executive (HSE)',
    publishDate: new Date('2025-05-15'),
    effectiveDate: new Date('2025-09-01'),
    summary:
      'New regulations requiring enhanced risk assessments for all construction activities and additional safety training for site personnel.',
    impactLevel: 'High',
    status: 'Unread',
    category: 'Health & Safety',
    details:
      'The Health and Safety Executive has published new regulations that will come into effect on September 1, 2025. These regulations introduce more stringent requirements for risk assessments on construction sites, including mandatory digital documentation and real-time updates. Additionally, all site personnel will be required to complete an enhanced safety training program before being allowed to work on site. Companies will have a 3-month grace period to implement these changes before enforcement actions begin.',
    actionRequired: true,
    relatedDocuments: [
      {
        id: 'd1',
        name: 'HSE_Regulations_2025.pdf',
        url: 'https://example.com/hse_regulations_2025.pdf',
      },
      {
        id: 'd2',
        name: 'Implementation_Guide.pdf',
        url: 'https://example.com/implementation_guide.pdf',
      },
    ],
  };

  const mockRelatedCertifications = [
    {
      id: 'c1',
      name: 'Health & Safety Compliance',
      status: 'Valid',
      expiryDate: new Date('2026-04-20'),
    },
    {
      id: 'c2',
      name: 'Construction Site Management',
      status: 'Valid',
      expiryDate: new Date('2025-11-15'),
    },
  ];

  // Load data on component mount

  // Load update details from API
  const loadFromApi = async () => {
    try {
      if (updateId) {
        const resp = await apiRequest(`regulatory-updates?id=${updateId}`);
        if (resp.success && resp.data) {
          setUpdateData(resp.data);
          if (resp.data.relatedCertifications) {
            setRelatedCertifications(resp.data.relatedCertifications);
          }
          setLoading(false);
          return true;
        }
      }
    } catch (error) {
      console.warn('API unavailable, using local data:', error.message);
    }
    return false;
  };

  useEffect(() => {
    loadFromApi().then(loaded => { if (loaded) return; });
    // Simulate API call
    setTimeout(() => {
      setUpdateData(mockUpdateData);
      setRelatedCertifications(mockRelatedCertifications);
      setLoading(false);
    }, 1000);
  }, [updateId]);

  const handleGoBack = () => {
    navigation.goBack();
  };

  const handleMarkAsRead = () => {
    setMarkingAsRead(true);

    // Simulate API call
    setTimeout(() => {
      setUpdateData({
        ...updateData,
        status: 'Read',
      });
      setMarkingAsRead(false);
    }, 1000);
  };

  const handleAddActionItem = () => {
    setActionTitle('');
    setActionDueDate(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)); // Default to 2 weeks from now
    setShowActionModal(true);
  };

  const handleSaveActionItem = async () => {
    if (!actionTitle.trim()) {
      alert('Please enter an action title.');
      return;
    }

    // Save action item via API
    try {
      await apiRequest('regulatory-updates', {
        method: 'POST',
        body: { update_id: updateId, title: actionTitle, due_date: actionDueDate.toISOString() }
      });
    } catch (apiError) {
      console.warn('API save failed:', apiError.message);
    }
    // Simulate API call to save action item
    setTimeout(() => {
      setShowActionModal(false);
      alert('Action item added successfully.');
    }, 500);
  };

  const handleDateChange = (event, selectedDate) => {
    setShowDatePicker(false);
    if (selectedDate) {
      setActionDueDate(selectedDate);
    }
  };

  const handleViewCertification = certificationId => {
    navigation.navigate('CertificationDetailsScreen', { certificationId });
  };

  const handleViewDocument = document => {
    // In a real app, this would open the document
    alert(`Opening document: ${document.name}`);
  };

  const formatDate = date => {
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getImpactLevelColor = level => {
    switch (level) {
      case 'High':
        return '#dc3545';
      case 'Medium':
        return '#ffc107';
      case 'Low':
        return '#28a745';
      default:
        return '#666';
    }
  };

  const renderDocumentItem = ({ item }) => (
    <TouchableOpacity
      style={styles.documentItem}
      onPress={() => handleViewDocument(item)}
    >
      <MaterialCommunityIcons name="file-pdf-box" size={24} color="#dc3545" />
      <Text style={styles.documentName}>{item.name}</Text>
      <MaterialCommunityIcons name="open-in-new" size={20} color="#0e4e96" />
    </TouchableOpacity>
  );

  const renderCertificationItem = ({ item }) => (
    <TouchableOpacity
      style={styles.certificationItem}
      onPress={() => handleViewCertification(item.id)}
    >
      <View style={styles.certificationHeader}>
        <Text style={styles.certificationName}>{item.name}</Text>
        <View
          style={[
            styles.statusBadge,
            item.status === 'Valid'
              ? styles.validStatus
              : item.status === 'Renewal Required'
                ? styles.renewalStatus
                : styles.expiredStatus,
          ]}
        >
          <Text style={styles.statusText}>{item.status}</Text>
        </View>
      </View>
      <Text style={styles.certificationExpiry}>
        Expires: {formatDate(item.expiryDate)}
      </Text>
    </TouchableOpacity>
  );

  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.header}>
          <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
            <Ionicons name="arrow-back" size={24} color="#fff" />
          </TouchableOpacity>
          <JobengLogo />
          <Text style={styles.headerTitle}>Regulatory Update</Text>
          <View style={styles.headerRight} />
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading update details...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
          <Ionicons name="arrow-back" size={24} color="#fff" />
        </TouchableOpacity>
        <JobengLogo />
        <Text style={styles.headerTitle}>Regulatory Update</Text>
        <View style={styles.headerRight} />
      </View>

      <ScrollView style={styles.container}>
        {/* Update Header */}
        <View style={styles.updateHeader}>
          <View style={styles.updateTitleContainer}>
            <Text style={styles.updateTitle}>{updateData.title}</Text>
            <View
              style={[
                styles.impactBadge,
                {
                  backgroundColor: `${getImpactLevelColor(updateData.impactLevel)}20`,
                },
              ]}
            >
              <Text
                style={[
                  styles.impactText,
                  { color: getImpactLevelColor(updateData.impactLevel) },
                ]}
              >
                {updateData.impactLevel} Impact
              </Text>
            </View>
          </View>
          <Text style={styles.updateSource}>{updateData.source}</Text>

          <View style={styles.updateDates}>
            <View style={styles.dateContainer}>
              <Text style={styles.dateLabel}>Published:</Text>
              <Text style={styles.dateValue}>
                {formatDate(updateData.publishDate)}
              </Text>
            </View>
            <View style={styles.dateContainer}>
              <Text style={styles.dateLabel}>Effective:</Text>
              <Text style={styles.dateValue}>
                {formatDate(updateData.effectiveDate)}
              </Text>
            </View>
          </View>

          <View style={styles.statusContainer}>
            <View
              style={[
                styles.statusIndicator,
                updateData.status === 'Read'
                  ? styles.readIndicator
                  : styles.unreadIndicator,
              ]}
            />
            <Text style={styles.statusLabel}>{updateData.status}</Text>
          </View>
        </View>

        {/* Update Summary */}
        <View style={styles.summaryCard}>
          <Text style={styles.sectionTitle}>Summary</Text>
          <Text style={styles.summaryText}>{updateData.summary}</Text>
        </View>

        {/* Update Details */}
        <View style={styles.detailsCard}>
          <Text style={styles.sectionTitle}>Details</Text>
          <Text style={styles.detailsText}>{updateData.details}</Text>
        </View>

        {/* Related Documents */}
        <View style={styles.documentsCard}>
          <Text style={styles.sectionTitle}>Related Documents</Text>

          {updateData.relatedDocuments &&
          updateData.relatedDocuments.length > 0 ? (
            <FlatList
              data={updateData.relatedDocuments}
              keyExtractor={item => item.id}
              renderItem={renderDocumentItem}
              scrollEnabled={false}
            />
          ) : (
            <View style={styles.emptyState}>
              <MaterialCommunityIcons
                name="file-document-outline"
                size={40}
                color="#ccc"
              />
              <Text style={styles.emptyStateText}>
                No related documents available
              </Text>
            </View>
          )}
        </View>

        {/* Related Certifications */}
        <View style={styles.certificationsCard}>
          <Text style={styles.sectionTitle}>Related Certifications</Text>

          {relatedCertifications.length > 0 ? (
            <FlatList
              data={relatedCertifications}
              keyExtractor={item => item.id}
              renderItem={renderCertificationItem}
              scrollEnabled={false}
            />
          ) : (
            <View style={styles.emptyState}>
              <MaterialCommunityIcons
                name="certificate-outline"
                size={40}
                color="#ccc"
              />
              <Text style={styles.emptyStateText}>
                No related certifications found
              </Text>
            </View>
          )}
        </View>

        {/* Action Buttons */}
        <View style={styles.actionButtonsContainer}>
          {updateData.status !== 'Read' && (
            <TouchableOpacity
              style={[styles.actionButton, styles.readButton]}
              onPress={handleMarkAsRead}
              disabled={markingAsRead}
            >
              {markingAsRead ? (
                <ActivityIndicator size={20} color="#fff" />
              ) : (
                <>
                  <MaterialCommunityIcons
                    name="check-circle-outline"
                    size={20}
                    color="#fff"
                  />
                  <Text style={styles.actionButtonText}>Mark as Read</Text>
                </>
              )}
            </TouchableOpacity>
          )}

          {updateData.actionRequired && (
            <TouchableOpacity
              style={[
                styles.actionButton,
                styles.actionItemButton,
                updateData.status !== 'Read'
                  ? { flex: 1, marginLeft: 10 }
                  : { flex: 1 },
              ]}
              onPress={handleAddActionItem}
            >
              <MaterialCommunityIcons
                name="playlist-plus"
                size={20}
                color="#fff"
              />
              <Text style={styles.actionButtonText}>Add Action Item</Text>
            </TouchableOpacity>
          )}
        </View>
      </ScrollView>

      {/* Add Action Item Modal */}
      <Modal visible={showActionModal} transparent={true} animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <Text style={styles.modalTitle}>Add Action Item</Text>

            <View style={styles.modalForm}>
              <Text style={styles.modalLabel}>Action Title</Text>
              <TextInput
                style={styles.modalInput}
                value={actionTitle}
                onChangeText={setActionTitle}
                placeholder="Enter action title"
              />

              <Text style={styles.modalLabel}>Due Date</Text>
              <TouchableOpacity
                style={styles.datePickerButton}
                onPress={() => setShowDatePicker(true)}
              >
                <Text style={styles.datePickerText}>
                  {formatDate(actionDueDate)}
                </Text>
                <Ionicons name="calendar-outline" size={20} color="#0e4e96" />
              </TouchableOpacity>

              {showDatePicker && (
                <DateTimePicker
                  value={actionDueDate}
                  mode="date"
                  display="default"
                  onChange={handleDateChange}
                  minimumDate={new Date()}
                />
              )}
            </View>

            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => setShowActionModal(false)}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.saveButton]}
                onPress={handleSaveActionItem}
              >
                <Text style={styles.saveButtonText}>Save</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#4A90E2',
  },
  header: {
    backgroundColor: '#4A90E2',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: 5,
  },
  headerTitle: {
    color: '#fff',
    fontSize: 20,
    fontWeight: 'bold',
    flex: 1,
    textAlign: 'center',
  },
  headerRight: {
    width: 24,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoImage: {
    width: 30,
    height: 30,
    marginRight: 5,
  },
  logoText: {
    color: '#fff',
    fontWeight: 'bold',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#666',
  },
  container: {
    flex: 1,
    padding: 15,
  },
  updateHeader: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 15,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  updateTitleContainer: {
    marginBottom: 5,
  },
  updateTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
  },
  impactBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 15,
    marginBottom: 10,
  },
  impactText: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  updateSource: {
    fontSize: 16,
    color: '#666',
    marginBottom: 10,
  },
  updateDates: {
    flexDirection: 'row',
    marginBottom: 10,
  },
  dateContainer: {
    marginRight: 20,
  },
  dateLabel: {
    fontSize: 12,
    color: '#666',
  },
  dateValue: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
  },
  statusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8f9fa',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    alignSelf: 'flex-start',
    marginTop: 5,
  },
  statusIndicator: {
    width: 10,
    height: 10,
    borderRadius: 5,
    marginRight: 8,
  },
  readIndicator: {
    backgroundColor: '#28a745',
  },
  unreadIndicator: {
    backgroundColor: '#ffc107',
  },
  statusLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
  },
  summaryCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 15,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 10,
  },
  summaryText: {
    fontSize: 15,
    color: '#333',
    lineHeight: 22,
  },
  detailsCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 15,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  detailsText: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
  documentsCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 15,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  documentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 12,
    marginBottom: 10,
  },
  documentName: {
    fontSize: 14,
    color: '#333',
    flex: 1,
    marginLeft: 10,
  },
  certificationsCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 15,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  certificationItem: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 12,
    marginBottom: 10,
  },
  certificationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 5,
  },
  certificationName: {
    fontSize: 15,
    fontWeight: '500',
    color: '#333',
    flex: 1,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
  },
  validStatus: {
    backgroundColor: 'rgba(40, 167, 69, 0.1)',
  },
  renewalStatus: {
    backgroundColor: 'rgba(255, 193, 7, 0.1)',
  },
  expiredStatus: {
    backgroundColor: 'rgba(220, 53, 69, 0.1)',
  },
  statusText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#333',
  },
  certificationExpiry: {
    fontSize: 13,
    color: '#666',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  emptyStateText: {
    fontSize: 14,
    color: '#666',
    marginTop: 10,
  },
  actionButtonsContainer: {
    flexDirection: 'row',
    marginBottom: 20,
  },
  actionButton: {
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 15,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
  },
  readButton: {
    backgroundColor: '#28a745',
  },
  actionItemButton: {
    backgroundColor: '#0e4e96',
  },
  actionButtonText: {
    color: '#fff',
    fontWeight: 'bold',
    marginLeft: 8,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 20,
    width: '90%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 15,
  },
  modalForm: {
    marginBottom: 20,
  },
  modalLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
    marginBottom: 8,
  },
  modalInput: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: '#333',
    marginBottom: 15,
  },
  datePickerButton: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  datePickerText: {
    fontSize: 16,
    color: '#333',
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  modalButton: {
    paddingVertical: 10,
    paddingHorizontal: 15,
    borderRadius: 5,
    marginLeft: 10,
  },
  cancelButton: {
    backgroundColor: '#f8f9fa',
  },
  saveButton: {
    backgroundColor: '#0e4e96',
  },
  cancelButtonText: {
    color: '#333',
    fontWeight: '500',
  },
  saveButtonText: {
    color: '#fff',
    fontWeight: '500',
  },
});

export default RegulatoryUpdateDetailsScreen;
